import { useState, useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import {
  SidebarLayout,
  Typography,
  Page,
  PageHeader,
  AddButtonGroup,
  Box,
  CuBadge,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Button,
  Tooltip,
  Stack,
} from "@flukecorp-emu/common-ui";
import { AddOutlined } from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  OpticalGrid,
  AuthTokenDialog,
  CreateOptiCalKeyDialog,
  EditOptiCalKeyDialog,
} from "../../components/Optical";
import OnboardAppDialog from "../../components/Optical/OnboardAppDialog";
import UsageStatisticsSection from "../../components/Optical/UsageStatisticsSection";
import { FilterListData } from "../../components/FilterList";
import {
  getOpticalData,
  getAppInfo,
  getUsageStatistics,
  getAuthToken,
  getOpticalDataByKey,
} from "../../services/opticalManager";

const opticalFilter = [
  {
    label: "Status",
    hasChild: true,
    value: "status",
    child: [
      { label: "All", value: "all" },
      { label: "Active", value: "active" },
      { label: "Suspended", value: "suspended" },
      { label: "Revoked", value: "revoked" },
    ],
  },
];

export const Route = createFileRoute("/_authenticated/optical")({
  component: Optical,
});

function Optical() {
  const { enqueueSnackbar } = useSnackbar();
  const searchParams = Route.useSearch();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editKeyData, setEditKeyData] = useState(null);
  const [isLoadingEditData, setIsLoadingEditData] = useState(false);
  const [isOnboardAppDialogOpen, setIsOnboardAppDialogOpen] = useState(false);
  const [isAuthTokenDialogOpen, setIsAuthTokenDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState("");

  // Default to "all" status if no status filter is selected
  const effectiveSearchParams = {
    ...searchParams,
    status: searchParams.status || "active",
  };

  // Check for auth token on component mount
  useEffect(() => {
    const authToken = getAuthToken();
    if (!authToken) {
      setIsAuthTokenDialogOpen(true);
    }
  }, []);

  // Fetch app info using React Query
  const { data: appInfo, refetch: refetchAppInfo } = useQuery({
    queryKey: ["appInfo"],
    queryFn: () => getAppInfo(),
    onError: (error) => {
      enqueueSnackbar(error?.message || "Failed to fetch app info", {
        variant: "error",
      });
    },
    enabled: !!getAuthToken(), // Only fetch if auth token exists
  });

  // Fetch optical data using React Query
  const { data: opticalData = {}, isLoading } = useQuery({
    queryKey: ["opticalData", effectiveSearchParams],
    queryFn: () => getOpticalData(effectiveSearchParams),
    onError: (error) => {
      enqueueSnackbar(error?.message || "Failed to fetch optical data", {
        variant: "error",
      });
    },
    enabled: !!getAuthToken(), // Only fetch if auth token exists
    refetchIntervalInBackground: 60000, // Refetch optical data every 60 seconds
  });

  // Fetch usage statistics using React Query
  const { data: usageStatistics = {}, isLoading: isStatisticsLoading } =
    useQuery({
      queryKey: ["usageStatistics"],
      queryFn: () => getUsageStatistics(),
      onError: (error) => {
        enqueueSnackbar(error?.message || "Failed to fetch usage statistics", {
          variant: "error",
        });
      },
      enabled: !!getAuthToken(), // Only fetch if auth token exists
      refetchIntervalInBackground: 60000, // Refetch usage statistics every 60 seconds
    });

  // Set selected app when appInfo loads
  useEffect(() => {
    if (appInfo?.data?.app?.app_id) {
      const currentAppId = appInfo.data.app.app_id;

      try {
        const storedAppId = localStorage.getItem("selectedAppId");

        // Validate stored app_id matches the current authenticated app
        // Since we only have one app from the API, always use the current one
        setSelectedApp(currentAppId);

        // Update localStorage only if it changed
        if (storedAppId !== currentAppId) {
          localStorage.setItem("selectedAppId", currentAppId);
        }
      } catch (error) {
        // Handle localStorage errors (private browsing, quota exceeded, etc.)
        console.warn("Failed to access localStorage:", error);
        setSelectedApp(currentAppId);
      }
    }
  }, [appInfo]);

  const handleAppChange = (event) => {
    const newAppId = event.target.value;
    setSelectedApp(newAppId);

    try {
      localStorage.setItem("selectedAppId", newAppId);
    } catch (error) {
      console.warn("Failed to save app selection to localStorage:", error);
    }
  };

  const handleCreate = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
  };

  const handleEdit = async (keyId) => {
    try {
      setIsLoadingEditData(true);
      const response = await getOpticalDataByKey(keyId);
      if (response) {
        setEditKeyData(response);
        setIsEditDialogOpen(true);
      } else {
        enqueueSnackbar("Failed to load key data for editing", {
          variant: "error",
        });
      }
    } catch (error) {
      enqueueSnackbar(
        error?.message || "Failed to fetch key data for editing",
        {
          variant: "error",
        },
      );
    } finally {
      setIsLoadingEditData(false);
    }
  };

  const handleCloseEditDialog = () => {
    setIsEditDialogOpen(false);
    setEditKeyData(null);
  };

  const handleCreateApp = () => {
    setIsOnboardAppDialogOpen(true);
  };

  const handleCloseOnboardAppDialog = () => {
    setIsOnboardAppDialogOpen(false);
  };

  const handleCloseAuthTokenDialog = () => {
    setIsAuthTokenDialogOpen(false);
  };

  const handleTokenSaved = () => {
    // Refetch app info after token is saved
    refetchAppInfo();
  };

  const styles = {
    header: {
      minHeight: "5rem",
      py: 1,
      borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
    },
  };

  return (
    <Box data-testid="optical-page">
      <SidebarLayout
        id="optical-sidebar"
        disableHoverExpand={true}
        data-testid="optical-sidebar-layout"
        sidebarHeader={
          <Typography
            component="div"
            variant="body1Bold"
            sx={{ pb: 1.5 }}
            data-testid="optical-sidebar-header"
          >
            Quick Filter
          </Typography>
        }
        sidebarContent={
          <FilterListData
            filterData={opticalFilter}
            searchParams={effectiveSearchParams}
            data-testid="optical-filter-list"
          />
        }
      >
        <Page
          disableGutters
          title="OptiCal Keys"
          data-testid="optical-page-main"
          pageHeight={"90vh"}
          pageHeader={
            <PageHeader
              hideMoreButton={true}
              position="sticky"
              title={
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  justifyContent="space-between"
                  flex={1}
                  pr={2}
                >
                  <Box>
                    <Typography variant="h4">OptiCal</Typography>
                    {/* <FormControl size="small" sx={{ minWidth: 200 }}>
                      <Select
                        value={selectedApp}
                        onChange={handleAppChange}
                        displayEmpty
                        sx={{
                          fontWeight: "bold",
                          height: "32px",
                        }}
                      >
                        {appInfo?.data?.app ? (
                          <MenuItem value={appInfo.data.app.app_id}>
                            {appInfo.data.app.app_name}
                          </MenuItem>
                        ) : (
                          <MenuItem value="">Loading...</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <Tooltip title="Add new App to configure the OptiCal keys">
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={true}
                        onClick={handleCreateApp}
                        sx={{ px: 1, minWidth: "32px", borderRadius: 0 }}
                      >
                        <AddOutlined fontSize="small" />
                      </Button>
                    </Tooltip> */}
                  </Box>
                  <UsageStatisticsSection
                    statistics={usageStatistics?.data}
                    isLoading={isStatisticsLoading}
                  />
                </Stack>
              }
              sx={styles.header}
              data-testid="optical-page-header"
              action={
                <AddButtonGroup
                  hideButtonDropdown={true}
                  label="Create New Key"
                  onClick={handleCreate}
                  data-testid="optical-create-button"
                />
              }
            />
          }
        >
          <OpticalGrid
            title={
              <CuBadge max={9999} badgeContent={opticalData?.keys?.length || 0}>
                OptiCal Keys
              </CuBadge>
            }
            isLoading={isLoading}
            opticalData={opticalData}
            handleCreate={handleCreate}
            handleEdit={handleEdit}
          />
        </Page>
      </SidebarLayout>

      {/* Create OptiCal Key Dialog */}
      <CreateOptiCalKeyDialog
        open={isCreateDialogOpen}
        onClose={handleCloseDialog}
      />

      {/* Edit OptiCal Key Dialog - Edit Mode */}
      <EditOptiCalKeyDialog
        open={isEditDialogOpen}
        editData={editKeyData}
        onClose={handleCloseEditDialog}
      />

      {/* Onboard App Dialog */}
      <OnboardAppDialog
        open={isOnboardAppDialogOpen}
        onClose={handleCloseOnboardAppDialog}
      />

      {/* Auth Token Dialog */}
      <AuthTokenDialog
        open={isAuthTokenDialogOpen}
        onClose={handleCloseAuthTokenDialog}
        onTokenSaved={handleTokenSaved}
      />
    </Box>
  );
}
